import axios from 'axios'
import { bffBaseURL, normalizeError } from './apiClient'

// ============================================================================
// Projects API — Phase 60 Milestone 9
// ============================================================================

export async function listProjects(params: Record<string, any> = {}) {
  try {
    const queryParams = new URLSearchParams()
    if (params.page) queryParams.set('page', params.page)
    if (params.limit) queryParams.set('limit', params.limit)
    if (params.client_id) queryParams.set('client_id', params.client_id)
    if (params.status) queryParams.set('status', params.status)
    if (params.search) queryParams.set('search', params.search)
    // F3.8.5: Added assignment_type param for M2-C1 completion
    if (params.assignment_type) queryParams.set('assignment_type', params.assignment_type)

    const queryString = queryParams.toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/projects${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to list projects')
      throw new Error(message)
    }
    throw error
  }
}

export async function getProject(projectId: string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/projects/${projectId}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get project')
      throw new Error(message)
    }
    throw error
  }
}

export async function createProject(data: any) {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/projects`,
      data
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to create project')
      throw new Error(message)
    }
    throw error
  }
}

export async function updateProject(projectId: string, data: any) {
  try {
    const response = await axios.patch(
      `${bffBaseURL}/api/v1/projects/${projectId}`,
      data
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to update project')
      throw new Error(message)
    }
    throw error
  }
}

export async function deleteProject(projectId: string) {
  try {
    await axios.delete(
      `${bffBaseURL}/api/v1/projects/${projectId}`
    )
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to delete project')
      throw new Error(message)
    }
    throw error
  }
}

// ============================================================================
// Project Assignments API — Phase 60 Milestone 9
// ============================================================================

export async function listAssignments(projectId: string, params: Record<string, any> = {}) {
  try {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.set('status', params.status)

    const queryString = queryParams.toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/projects/${projectId}/assignments${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to list assignments')
      throw new Error(message)
    }
    throw error
  }
}

export async function createAssignment(projectId: string, data: any) {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/projects/${projectId}/assignments`,
      data
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to create assignment')
      throw new Error(message)
    }
    throw error
  }
}

export async function updateAssignment(projectId: string, assignmentId: string, data: any) {
  try {
    const response = await axios.patch(
      `${bffBaseURL}/api/v1/projects/${projectId}/assignments/${assignmentId}`,
      data
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to update assignment')
      throw new Error(message)
    }
    throw error
  }
}

export async function releaseAssignment(projectId: string, assignmentId: string, notes: string | null = null) {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/projects/${projectId}/assignments/${assignmentId}/release`,
      notes ? { notes } : {}
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to release assignment')
      throw new Error(message)
    }
    throw error
  }
}

export async function deleteAssignment(projectId: string, assignmentId: string) {
  try {
    await axios.delete(
      `${bffBaseURL}/api/v1/projects/${projectId}/assignments/${assignmentId}`
    )
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to delete assignment')
      throw new Error(message)
    }
    throw error
  }
}

export async function getUpcomingReleases(days: number = 30) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/releases/upcoming`,
      { params: { days } }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get releases')
      throw new Error(message)
    }
    throw error
  }
}
